// Toasts: transient outcome messages for actions that don't have an inline
// place to report (row actions, navigation after create/delete). Per-field
// saves use SaveStatus instead.
//
//   const toast = useToast()
//   toast.success('Tenant created')
//   toast.error("Couldn't delete: …")

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { isSessionEnded } from '@/api/client'
import { cn } from '@/lib/format'
import { AlertIcon, CheckIcon, InfoIcon, XIcon } from './icons'
import { ToastContext, type ToastApi, type ToastItem, type ToastTone } from './toastContext'

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((t) => t.id !== id)), [])
  const push = useCallback((tone: ToastTone, message: ReactNode, duration: number) => {
    const id = nextId.current++
    setItems((xs) => [...xs.slice(-3), { id, tone, message, duration }])
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, o) => push('success', m, o?.duration ?? 4000),
      // Nothing while the session has ended: ToastProvider sits above the
      // routes, so a save's 401 toast would otherwise show on /login.
      error: (m, o) => {
        if (!isSessionEnded()) push('error', m, o?.duration ?? 8000)
      },
      info: (m, o) => push('info', m, o?.duration ?? 4000),
      dismiss,
    }),
    [push, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        {items.map((t) => (
          <Toast key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    const t = setTimeout(() => onDismiss(item.id), item.duration)
    return () => clearTimeout(t)
  }, [item.id, item.duration, onDismiss])

  const Icon = item.tone === 'success' ? CheckIcon : item.tone === 'error' ? AlertIcon : InfoIcon
  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm animate-toast-in items-start gap-2.5 rounded-lg border bg-raised px-3.5 py-3 text-[13px] text-ink shadow-toast',
        item.tone === 'error' ? 'border-bad/40' : item.tone === 'success' ? 'border-good/30' : 'border-line-strong',
      )}
    >
      <Icon size={16} className={cn('mt-0.5 shrink-0', item.tone === 'error' ? 'text-bad' : item.tone === 'success' ? 'text-good' : 'text-muted')} />
      <div className="min-w-0 flex-1">{item.message}</div>
      <button type="button" onClick={() => onDismiss(item.id)} aria-label="Dismiss" className="-m-1 rounded p-1 text-muted hover:bg-hover hover:text-ink">
        <XIcon size={14} />
      </button>
    </div>
  )
}
